import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import EventsApi from "../../../api/instance/EventsApi";
import InstanceApi from "../../../api/instance/InstanceApi";
import ActivityApi from "../../../api/instance/ActivityApi";

// Components.
import StatementTimeChart from "../../../component/instance/chart/StatementTimeChart";
import HostsOverTimeCharts from "../../../component/instance/chart/HostsOverTimeCharts";
import GroupedStatementsTable from "../../../component/instance/table/GroupedStatementsTable";
import StatementsTable from "../../../component/instance/table/StatementsTable";
import ClientsTable from "../../../component/instance/table/ClientsTable";
import PieChart from "../../../component/PieChart";
import SessionsTable from "../../../component/instance/table/SessionsTable";
import DatabasesTable from "../../../component/instance/table/DatabasesTable";
import WaitsTable from "../../../component/instance/table/WaitsTable";
import ProgramsTable from "../../../component/instance/table/ProgramsTable";
import UsersTable from "../../../component/instance/table/UsersTable";
import StatementDetailsPanel from "../../instances/tabs/StatementDetailsPanel";

// Contexts
import { HostContext } from "../../../context/HostContext";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery, buildApiQuery } from "../../../helpers/timeRangeUtils";
import { FilterContext } from "../../../context/FilterContext";
import { useEventTypes } from "../../../context/EventTypeContext";

// Third-party packages.

// Helpers.
import Helper from "../../../helpers/Helper";
import { getAPIString, getStatementTypeFromURL, StatementTypeInfo } from "../../instances/tabs/utils";
import ConditionalRender from "../../../helpers/ConditionalRender";
import { getFilterParameters, getConvertedTimeToUTC } from "../../../helpers/utils";
import TimeSeriesChart from "../../../component/chart/echarts/TimeSeriesChart";
import { tooltipRow, wrapTooltip } from "../../../component/chart/echarts/tooltipAdapter";
import { EChartBandPoint, EChartPoint, EChartTimeSeries } from "../../../component/chart/echarts/types";

// Types.
import Wait from "../../../types/instance/Wait";
import CombinedChanges from "../../../types/instance/CombinedChanges";
import ChartPlotLine from "../../../types/ChartPlotLine";
import EventType from "../../../types/instance/EventType";
import InstanceTarget from "../../../types/instance/InstanceTarget";
import DatabaseTime from "../../../types/instance/DatabaseTime";
import HostTarget from "../../../types/host/HostTarget";
import PieChartSeries from "../../../types/PieChartSeries";
import Database from "../../../types/instance/Database";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import Client from "../../../types/instance/Client";
import Statement from "../../../types/instance/Statement";
import StatementsDataTable from "../../../types/instance/tables/StatementsDataTable";
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import GroupedStatement from "../../../types/instance/GroupedStatement";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";
import Session from "../../../types/instance/Session";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import Program from "../../../types/instance/Program";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import User from "../../../types/instance/User";
import WaitsDataTable from "../../../types/instance/tables/WaitsDataTable";
import GroupedStatementsDataTable from "../../../types/instance/tables/GroupedStatementsDataTable";
import ChartOption from "../../../types/instance/ChartOption";
import { FilterType } from "../../../typescript/Enums";


// Constants.
import {
    CHART_COLOURS_STATEMENTS,
    CHART_COLOURS_WAITS,
    chartOptionsList,
    INSTANCE_TYPE_ORACLE,
    INSTANCE_TYPE_SQLSERVER,
    STATEMENT_CHART_DATA_OPTION,
    CHART_DATA_TYPE
} from "../../../component/Constants";
import WidgetCard from "../../../component/instance/widget";
import FilterBar from "../../../component/FilterBar";

interface IStatementActivityProps {
    statementId?: string,
    batchId?: string,
    batchStatementId?: string,
    instance: InstanceTarget,
    setFilterOptions: Function,
    eventTypes: EventType[],
    tabIdPrefix?: string, // Optional prefix to make tab IDs unique (e.g., for slide-out panels)
    statementType?: StatementTypeInfo // Optional statement type info (required for panel mode since URL doesn't change)
}

const StatementActivity = (props: IStatementActivityProps) => {
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh
    const { hosts } = useContext(HostContext);
    const { eventTypes } = useEventTypes();
    const { currentFilterOptions } = useContext(FilterContext);

    const {
        instance,
        statementId,
        batchId,
        batchStatementId,
        setFilterOptions,
        tabIdPrefix = '',
        statementType: providedStatementType
    } = props;

    // Parse URL parameters for baseline metrics
    const urlParams = new URLSearchParams(window.location.search);
    const waitTimeParam = parseFloat(urlParams.get('waittime') || '0');
    const baselineWaitTimeParam = parseFloat(urlParams.get('baselinewaittime') || '0');
    const baselineStdDevWaitTimeParam = parseFloat(urlParams.get('baselinestddevwaittime') || '0');

    const filterParameters = getFilterParameters(currentFilterOptions);

    const chartSeriesCeiling = Number(process.env.REACT_APP_CHART_SERIES_CEILING);
    // Use provided statement type if available (for panel mode), otherwise get from URL
    const statementType = providedStatementType || getStatementTypeFromURL()

    const [statements, setStatements] = useState<Statement[]>([]);
    const [groupedStatements, setGroupedStatements] = useState<GroupedStatement[]>([]);
    const [optionsList, setOptionsList] = useState(chartOptionsList);
    const [timeChartDataOption, setTimeChartDataOption] = useState(STATEMENT_CHART_DATA_OPTION.ACTIVITY);
    const [timeChartDataTitle, setTimeChartDataTitle] = useState('Activity Chart');

    const [statementsDataTable, setStatementsDataTable] = useState<StatementsDataTable[]>([]);
    const [groupedStatementsDataTable, setGroupedStatementsDataTable] = useState<GroupedStatementsDataTable[]>([]);
    const [waitsDataTable, setWaitsDataTable] = useState<WaitsDataTable[]>([]);
    const [clientsDataTable, setClientDataTable] = useState<ClientsDataTable[]>([]);
    const [databasesDataTable, setDatabasesDataTable] = useState<DatabasesDataTable[]>([]);
    const [sessionsDataTable, setSessionsDataTable] = useState<SessionsDataTable[]>([]);
    const [programsDataTable, setProgramsDataTable] = useState<ProgramsDataTable[]>([]);
    const [usersDataTable, setUsersDataTable] = useState<UsersDataTable[]>([]);

    // const [batchesDataTable, setBatchesTableData] = useState<any[]>([]);
    // const [groupedBatchesDataTable, setGroupedBatchesTableData] = useState<GroupedBatchesDataTable[]>([]);

    // const [statementsPieChartSeries, setStatementsPieChartSeries] = useState<PieChartSeries[]>([]);
    // const [groupStatementsPieChartSeries, setGroupStatementsPieChartSeries] = useState<PieChartSeries[]>([]);

    const [waitsPieChartSeries, setWaitsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [clientsPieChartSeries, setClientsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [databasesPieChartSeries, setDatabasesPieChartSeries] = useState<PieChartSeries[]>([]);
    const [programsPieChartSeries, setProgramsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [sessionsPieChartSeries, setSessionsPieChartSeries] = useState<PieChartSeries[]>([]);
    const [usersPieChartSeries, setUsersPieChartSeries] = useState<PieChartSeries[]>([]);

    const [statementTime, setStatementTime] = useState<number>(0);
    const [statementTimePrevious, setStatementTimePrevious] = useState<number>(0);
    const [statementCombinedChanges, setStatementCombinedChanges] = useState<CombinedChanges[]>([]);

    const [statementChartPlotLines, setStatementChartPlotLines] = useState<ChartPlotLine[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [, setBackgroundLoading] = useState<boolean>(false);

    // Time series for baseline comparison chart
    const [activityTimeSeries, setActivityTimeSeries] = useState<number[][]>([]);

    const [displayHostMetrics, setDisplayHostMetrics] = useState<boolean>(false);
    const [availableHosts, setAvailableHosts] = useState<HostTarget[]>([]);

    const [visibleTabs, setVisibleTabs] = useState<string[]>([]);

    // State for statement details panel (only used when not already in a panel)
    const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
    const [selectedStatementType, setSelectedStatementType] = useState<'statement' | 'groupedstatement' | 'batch' | 'groupedbatch'>('statement');
    const [panelOpen, setPanelOpen] = useState(false);

    // Panel handlers
    const openPanel = (statementId: string, statementType: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch' = 'statement') => {
        setSelectedStatement(statementId);
        setSelectedStatementType(statementType);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedStatement(null);
    };

    useEffect(() => {
        let updatedList = [...chartOptionsList];
        updatedList.splice(3, 0, {
            label: instance.type === INSTANCE_TYPE_ORACLE ? 'Schemas Chart' : 'Databases Chart',
            value: STATEMENT_CHART_DATA_OPTION.DATABASES
        });

        const updatedChartOptionsList = updatedList.filter(
            option => option.label !== "Statements Chart"
        );

        setOptionsList(updatedChartOptionsList);
    }, [instance.type]);

    useEffect(() => {
        let tabs: string[] = [];
        if (instance.type === INSTANCE_TYPE_SQLSERVER) {
            switch (statementType.type) {
                case 'statement':
                    tabs = [];
                    break;
                case 'groupedstatement':
                    tabs = ['statements'];
                    break;
                case 'batch':
                    tabs = ['statements'];
                    break;
                case 'groupedbatch':
                    tabs = ['groupedstatements'];
                    break;
                default:
                    tabs = [];
                    break;
            }
        } else {
            tabs = statementType.type === 'groupedstatement' ? ['statements'] : [];
        }

        setVisibleTabs(tabs);
    }, []);

    // Only fetch time series if we have baseline params (to avoid duplicate API call with StatementTimeChart)
    const getActivityTimeSeries = async (instanceId: number, urlString: string) => {
        // Skip if no baseline params - StatementTimeChart already fetches this data
        if (waitTimeParam <= 0 && baselineWaitTimeParam <= 0) return;

        try {
            const response = await ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), instanceId, `${urlString}${filterParameters}`);
            const timeSeries = response.data.map((item: any) => [getConvertedTimeToUTC(item), item.waittime]);
            setActivityTimeSeries(timeSeries);
        } catch (error) {
            console.error('Failed to retrieve activity time series:', error);
        }
    };

    useEffect(() => {
        let isActive = true;

        const getData = async () => {
            const urlString = getAPIString(statementId || batchId, batchStatementId, statementType);

            try {
                // ============================================================
                // PHASE 1: Critical Data (Blocks Initial Render)
                // ============================================================
                // Fetch only the data needed for the default view:
                // - Waits data (for default Waits table)
                // - Instance time (for widgets)
                // - Changes (for plot lines)
                // - Instance hosts (for host metrics toggle)

                await Promise.all([
                    getWaits(instance.id, urlString, isActive),
                    getInstanceTime(instance.id, urlString, isActive),
                    getChanges(instance.id, isActive),
                    getInstanceHosts(instance.id, isActive),
                    await getActivityTimeSeries(instance.id, urlString)
                ]);

                // Fetch statements/grouped statements based on type
                if (isActive) {
                    if (statementType.isGroup && statementType.isBatch) {
                        await getGroupedStatements(instance.id, isActive);
                    } else {
                        await getStatements(instance.id, isActive);
                    }
                }

                // ============================================================
                // RENDER CHECKPOINT: Component can now render
                // ============================================================
                if (isActive) {
                    setLoading(false);
                }

                // ============================================================
                // PHASE 2: Background Data (Non-Blocking)
                // ============================================================
                // Fetch remaining data in the background without blocking UI

                if (isActive) {
                    setBackgroundLoading(true);

                    await Promise.all([
                        getUsers(instance.id, urlString, isActive),
                        getClients(instance.id, urlString, isActive),
                        getSessions(instance.id, urlString, isActive),
                        getDatabases(instance.id, urlString, isActive),
                        getPrograms(instance.id, urlString, isActive),
                    ]);

                    setBackgroundLoading(false);
                }

            } catch (error) {
                if (isActive) {
                    console.error('Failed to load activity data:', error);
                    setLoading(false);
                    setBackgroundLoading(false);
                }
            }
        };

        setLoading(true);
        if (instance) {
            void getData();
        }

        return () => {
            isActive = false;
        };
    }, [instance, statementId, filterParameters]);

    const getInstanceHosts = async (instanceId: number, isActive: boolean = true) => {

        let instanceHosts: HostTarget[] = [];
        // Get any hosts related to this instance.
        const hostMappings = await InstanceApi.getInstanceHostByInstance(instanceId);
        if (isActive) {
            for (let index = 0; index < hostMappings.length; index++) {
                const matchedHosts = hosts.filter(host => host.id === hostMappings[index].hostid);
                if (matchedHosts.length === 1) {
                    instanceHosts.push(matchedHosts[0]);
                }
            }
            setAvailableHosts(instanceHosts);
        }
    }

    const getInstanceTime = async (instanceId: number, apiString: string, isActive: boolean = true) => {

        // Get total batch/statement time with time series data.
        ActivityApi.getSummary(buildApiQuery(timeRangeContext.timeRange), instanceId, `${apiString}${filterParameters}`)
            .then((response: { data: DatabaseTime[]; }) => {
                if (!isActive) return;
                if (response.data.length > 0) {
                    setStatementTime(response.data[0].waittime)
                }
            })
            .catch((error: any) => {
                if (!isActive) return;
                console.log('An error occurred:', error);
            })

        const comparisonSupported = Helper.isComparisonSupported(timeRangeContext)
        if (comparisonSupported) {
            // Get total database time for the previous period.
            ActivityApi.getSummary(buildApiQuery(timeRangeContext.timeRange, true), instanceId, `${apiString}${filterParameters}`)
                .then((response: { data: DatabaseTime[]; }) => {
                    if (!isActive) return;
                    if (response.data.length > 0) {
                        setStatementTimePrevious(response.data[0].waittime)
                    }
                })
                .catch((error: any) => {
                    if (!isActive) return;
                    console.error('Failed to retrieve total instance time.', error);
                })
        }
    }

    const getChanges = async (instanceId: number, isActive: boolean = true) => {

        let allEvents: any[] = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instanceId),
            combinedChanges: CombinedChanges[] = [],
            chartPlotLines: ChartPlotLine[] = [];

        if (!isActive) return;

        for (let index = 0; index < allEvents.length; index++) {
            const currentItem = allEvents[index];
            combinedChanges.push({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            });
        }

        // Set the sort order for the combined changes and events.
        combinedChanges.sort(function compare(a, b) {
            var aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
            var bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
            return bb - aa;
        });

        const interval = Helper.getInterval(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, timeRangeContext.timeRange?.interval)
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange?.from, timeRangeContext.timeRange?.to, interval)
        // Pass ID with type prefix for proper URL construction (e.g., "batch:123" or "groupedbatch:456")
        const eventId = statementId || (batchId ? `${statementType?.type || 'batch'}:${batchId}` : undefined);
        chartPlotLines = Helper.getChartPlotLines(buildRedirectQuery(timeRangeContext.timeRange), combinedChanges, chartCategories, interval, false, eventId, eventTypes);

        if (isActive) {
            setStatementCombinedChanges(combinedChanges)
            setStatementChartPlotLines(chartPlotLines)
        }
    }

    const getWaits = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        ActivityApi.getWaitEvents(buildApiQuery(timeRangeContext.timeRange), instanceId, `${urlString}${filterParameters}`)
            .then(async (response: { data: Wait[]; }) => {
                if (!isActive) return;

                let waitsDataTable: WaitsDataTable[] = [],
                    topWaitsChartSeries: PieChartSeries[] = [];

                response.data.sort((a: any, b: any) => {
                    const aWait = Number(a.waittime) || 0;
                    const bWait = Number(b.waittime) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    return a.waitevent.localeCompare(b.waitevent);
                });

                // Build top waits pie chart and data table in a single loop.
                for (let index = 0; index < response.data.length; index++) {
                    const item = response.data[index];

                    // Set the top colours for the chart if available.
                    if (index < CHART_COLOURS_WAITS.length) {
                        item.color = CHART_COLOURS_WAITS[index];
                    }

                    // Push to the pie chart series.
                    topWaitsChartSeries.push({
                        color: item.color,
                        formatted: Helper.getTimeInEnglish(item.waittime),
                        name: item.waitevent,
                        y: item.waittime
                    });

                    // Push to the data table.
                    waitsDataTable.push({
                        id: index,
                        color: item.color,
                        waitevent: item.waitevent,
                        waittime: item.waittime
                    });
                }

                if (response.data.length > chartSeriesCeiling) {

                    // Get and add a remaining category to the chart.
                    const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                        .map((item: Wait) => item.waittime)
                        .reduce((a: number, b: number) => a + b, 0);


                    topWaitsChartSeries.push({
                        color: CHART_COLOURS_WAITS[chartSeriesCeiling],
                        formatted: Helper.getTimeInEnglish(remainingTime),
                        name: 'Other Waits',
                        y: remainingTime
                    });
                }

                if (isActive) {
                    setWaitsDataTable(waitsDataTable)
                    setWaitsPieChartSeries(topWaitsChartSeries.slice(0, chartSeriesCeiling))
                    setLoading(false)
                }
            })
            .catch((error: any) => {
                if (!isActive) return;
                console.log('An error occurred:', error);
            })
    }

    const getStatements = (instanceId: number, isActive: boolean = true) => {
        const apiString = getAPIString(statementId || batchId, batchStatementId, statementType)
        ActivityApi.getDimension('sql', buildApiQuery(timeRangeContext.timeRange), instanceId, `${apiString}${filterParameters}`)
            .then((response: { data: Statement[]; }) => {
                if (!isActive) return;

                let statementsDataTable: StatementsDataTable[] = [];
                // statementsPieChartSeries: PieChartSeries[] = [];

                response.data.sort((a: any, b: any) => {
                    const aWait = Number(a.waittime) || 0;
                    const bWait = Number(b.waittime) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    const aHash = Number(a.sqlhash) || 0;
                    const bHash = Number(b.sqlhash) || 0;

                    return aHash - bHash;
                });

                // Build top statements pie chart.
                for (let index = 0; index < response.data.length; index++) {

                    if (index < CHART_COLOURS_STATEMENTS.length) {
                        // Set the top colours for the chart.
                        response.data[index].color = CHART_COLOURS_STATEMENTS[index];
                    }

                    statementsDataTable.push({
                        id: index,
                        color: ((index < CHART_COLOURS_STATEMENTS.length) ? CHART_COLOURS_STATEMENTS[index] : ''),
                        sqlhash: response.data[index].sqlhash,
                        sqltext: response.data[index].sqltext,
                        executions: ((response.data[index].executions === undefined) ? null : Number(response.data[index].executions)),
                        waittime: response.data[index].waittime,
                        averagetime: ((response.data[index].executions === undefined) ? null : Number((response.data[index].waittime / Number(response.data[index].executions)).toFixed(2))),
                        statement: response.data[index]
                    });

                    // statementsPieChartSeries.push({
                    //     color: response.data[index].color,
                    //     formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    //     name: response.data[index].sqlhash,
                    //     y: response.data[index].waittime,
                    //     url: `/instances/${instanceId}/activity/statement/${response.data[index].sqlhash}`
                    // });
                }

                // if (response.data.length > chartSeriesCeiling) {
                //
                //     Get and add a remaining category to the chart.
                //     const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                //             .map((item: Statement) => item.waittime)
                //             .reduce((a: number, b: number) => a + b, 0);
                //
                //     statementsPieChartSeries.push({
                //         color: '#5e4fa2',
                //         formatted: Helper.getTimeInEnglish(remainingTime),
                //         name: 'Other Statements',
                //         y: remainingTime
                //     });
                // }

                if (isActive) {
                    setStatements(response.data)
                    setStatementsDataTable(statementsDataTable)
                }
                // setStatementsPieChartSeries(statementsPieChartSeries.slice(0, chartSeriesCeiling))
            })
    }

    const getGroupedStatements = (instanceId: number, isActive: boolean = true) => {
        ActivityApi.getSqlGroup(buildApiQuery(timeRangeContext.timeRange), instanceId, `&batchgrouphash=${batchId}${filterParameters}`)
            .then((response: { data: GroupedStatement[]; }) => {
                if (!isActive) return;

                let statementsDataTable: GroupedStatementsDataTable[] = [];
                // statementsPieChartSeries: PieChartSeries[] = [];

                response.data.sort((a: any, b: any) => {
                    const aWait = Number(a.waittime) || 0;
                    const bWait = Number(b.waittime) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    const aHash = Number(a.grouphash) || 0;
                    const bHash = Number(b.grouphash) || 0;

                    return aHash - bHash;
                });

                // Build top statements pie chart.
                for (let index = 0; index < response.data.length; index++) {

                    if (index < CHART_COLOURS_STATEMENTS.length) {
                        // Set the top colours for the chart.
                        response.data[index].color = CHART_COLOURS_STATEMENTS[index];
                    }

                    statementsDataTable.push({
                        id: index,
                        color: ((index < CHART_COLOURS_STATEMENTS.length) ? CHART_COLOURS_STATEMENTS[index] : ''),
                        grouphash: response.data[index].grouphash,
                        sqltext: response.data[index].sqltext,
                        executions: ((response.data[index].executions === undefined) ? null : Number(response.data[index].executions)),
                        waittime: response.data[index].waittime,
                        averagetime: ((response.data[index].executions === undefined) ? null : Number((response.data[index].waittime / Number(response.data[index].executions)).toFixed(2))),
                        statement: response.data[index]
                    });

                    // statementsPieChartSeries.push({
                    //     color: response.data[index].color,
                    //     formatted: Helper.getTimeInEnglish(response.data[index].waittime),
                    //     name: response.data[index].grouphash,
                    //     y: response.data[index].waittime,
                    //     url: `/instances/${instanceId}/activity/groupedbatch/${batchId}/groupedstatement/${response.data[index].grouphash}`
                    // });
                }

                // if (response.data.length > chartSeriesCeiling) {
                //
                //     // Get and add a remaining category to the chart.
                //     const remainingTime = response.data.slice(chartSeriesCeiling, response.data.length)
                //             .map((item: GroupedStatement) => item.waittime)
                //             .reduce((a: number, b: number) => a + b, 0);
                //
                //     statementsPieChartSeries.push({
                //         color: '#5e4fa2',
                //         formatted: Helper.getTimeInEnglish(remainingTime),
                //         name: 'Other Statements',
                //         y: remainingTime
                //     });
                // }

                if (isActive) {
                    setGroupedStatements(response.data)
                    setGroupedStatementsDataTable(statementsDataTable)
                }
                // setGroupStatementsPieChartSeries(statementsPieChartSeries.slice(0, chartSeriesCeiling))
            })
    }

    // const getBatches = async (instanceId: number) => {
    //     let dataTable: BatchesDataTable[] = [];
    //             // pieChartSeries: PieChartSeries[] = [];
    //
    //     let apiString = ''
    //     const statementType = getStatementTypeFromURL()
    //
    //     if (statementType.type === 'statement') {
    //         apiString = `&sqlhash=${props.batchStatementId || props.statementId}`
    //     }
    //     if (statementType.type === 'groupedstatement') {
    //         apiString = `&grouphash=${props.batchStatementId || props.statementId}`
    //     }
    //     if (statementType.type === 'groupedbatch') {
    //         apiString = `&batchgrouphash=${props.statementId}`
    //     }
    //
    //     await api.get(`activity/batch/?limit=${process.env.REACT_APP_API_LIMIT}&${buildApiQuery(timeRangeContext.timeRange)}&sort=waittime+desc&id=${instanceId}${apiString}${filterParameters}`)
    //             .then(async (response: { data: Batch[] }) => {
    //
    //                 for (let index = 0; index < response.data.slice(0, Number(process.env.REACT_APP_API_LIMIT) - 1).length; index++) {
    //
    //                     let batchwaittime: number = 0;
    //
    //                     if (props.statementId !== undefined || props.batchId !== undefined) {
    //
    //                         // Get the total batch time
    //                         await api.get(`activity/batchsqlhash?limit=${process.env.REACT_APP_API_LIMIT}&${buildApiQuery(timeRangeContext.timeRange)}&sort=waittime+desc&id=${instanceId}${apiString}${filterParameters}`)
    //                                 .then((batchResponse: { data: Batch[], status: number }) => {
    //                                     if (batchResponse.status === 200 && batchResponse.data.length > 0) {
    //                                         batchwaittime = batchResponse.data[0].waittime;
    //                                     }
    //                                 })
    //                                 .catch((error: any) => {
    //                                     console.error('Failed to retrieve batch without statement ID.', error, response.data[index].batchsqlhash);
    //                                 })
    //                     }
    //
    //                     dataTable.push({
    //                         id: index,
    //                         color: index < CHART_COLOURS_STATEMENTS.length ?
    //                                 CHART_COLOURS_STATEMENTS[index] : '',
    //                         batchsqlhash: response.data[index].batchsqlhash,
    //                         sqltext: response.data[index].sqltext,
    //                         executions: response.data[index].batchexecutions,
    //                         averagetime: Number((response.data[index].waittime / Number(response.data[index].batchexecutions)).toFixed(2)),
    //                         waittime: response.data[index].waittime,
    //                         batchwaittime,
    //                         batch: response.data[index]
    //                     });
    //                 }
    //
    //                 // Build top batches pie chart.
    //                 // for (let index = 0; index < response.data.slice(0, Number(process.env.REACT_APP_CHART_SERIES_CEILING)).length; index++) {
    //                 //
    //                 //     let name: string = ((response.data[index].batchsqlhash === null) ? '-' : response.data[index].batchsqlhash);
    //                 //     if (name.length > Number(process.env.REACT_APP_CHART_LEGEND_LENGTH)) {
    //                 //         name = `${name.substring(0, Number(process.env.REACT_APP_CHART_LEGEND_LENGTH))}...`;
    //                 //     }
    //                 //
    //                 //     pieChartSeries.push({
    //                 //         color: CHART_COLOURS_STATEMENTS[index],
    //                 //         formatted: Helper.getTimeInEnglish(response.data[index].waittime),
    //                 //         name,
    //                 //         y: response.data[index].waittime,
    //                 //         url: ((response.data[index].batchsqlhash !== null && response.data[index].batchsqlhash !== '-') ? `/instances/${instanceId}/activity/batch/${response.data[index].batchsqlhash}` : null)
    //                 //     });
    //                 // }
    //                 // setBatchesTableData(dataTable)
    //                 // setBatchesPieChartSeries(pieChartSeries)
    //
    //             })
    //             .catch((error: any) => {
    //                 console.error('Failed to retrieve batches.', error);
    //             })
    // }

    // const getGroupedBatches = async (instanceId: number) => {
    //     let dataTable: GroupedBatchesDataTable[] = [],
    //             pieChartSeries: PieChartSeries[] = [];
    //
    //     await api.get(`activity/batch/group?limit=${process.env.REACT_APP_API_LIMIT}&${buildApiQuery(timeRangeContext.timeRange)}&sort=waittime+desc&id=${instanceId}&grouphash=${props.statementId}${filterParameters}`)
    //             .then(async (response: { data: ActivityBatchGroup[] }) => {
    //                 for (let index = 0; index < response.data.slice(0, Number(process.env.REACT_APP_API_LIMIT) - 1).length; index++) {
    //                     let batchwaittime: number = 0;
    //
    //                     if (props.statementId !== undefined) {
    //
    //                         // Get the total batch time without the statement parameter.
    //                         await api.get(`activity/summary?limit=${process.env.REACT_APP_API_LIMIT}&${buildApiQuery(timeRangeContext.timeRange)}&sort=waittime+desc&batchgrouphash=${response.data[index].batchgrouphash}${filterParameters}`)
    //                                 .then((batchResponse: { data: ActivityBatchGroup[], status: number }) => {
    //                                     if (batchResponse.status === 200 && batchResponse.data.length > 0) {
    //                                         batchwaittime = batchResponse.data[0].waittime;
    //                                     }
    //                                 })
    //                                 .catch((error: any) => {
    //                                     console.error('Failed to retrieve batch without statement ID.', error, response.data[index].batchgrouphash);
    //                                 })
    //                                 .then(function () {
    //                                 });
    //
    //                     }
    //
    //                     dataTable.push({
    //                         id: index,
    //                         color: index < CHART_COLOURS_STATEMENTS.length ?
    //                                 CHART_COLOURS_STATEMENTS[index] : '',
    //                         batchgrouphash: response.data[index].batchgrouphash,
    //                         sqltext: response.data[index].sqltext,
    //                         executions: response.data[index].batchexecutions,
    //                         averagetime: Number((response.data[index].waittime / Number(response.data[index].batchexecutions)).toFixed(2)),
    //                         waittime: response.data[index].waittime,
    //                         batchwaittime,
    //                         batch: response.data[index]
    //                     });
    //                 }
    //                 // Build top batches pie chart.
    //                 for (let index = 0; index < response.data.slice(0, Number(process.env.REACT_APP_CHART_SERIES_CEILING)).length; index++) {
    //
    //                     let name: string = ((response.data[index].batchgrouphash === null) ? '-' : response.data[index].batchgrouphash);
    //
    //                     if (name.length > Number(process.env.REACT_APP_CHART_LEGEND_LENGTH)) {
    //                         name = `${name.substring(0, Number(process.env.REACT_APP_CHART_LEGEND_LENGTH))}...`;
    //                     }
    //
    //                     pieChartSeries.push({
    //                         color: CHART_COLOURS_STATEMENTS[index],
    //                         formatted: Helper.getTimeInEnglish(response.data[index].waittime),
    //                         name,
    //                         y: response.data[index].waittime,
    //                         url: ((response.data[index].batchgrouphash !== null && response.data[index].batchgrouphash !== '-') ? `/instances/${instanceId}/activity/groupedbatch/${response.data[index].batchgrouphash}` : null)
    //                     });
    //                 }
    //                 setBatchesTableData(dataTable);
    //                 // setBatchesPieChartSeries(pieChartSeries)
    //
    //             })
    //             .catch((error: any) => {
    //                 console.error('Failed to retrieve batches.', error);
    //             })
    // }

    const fetchData = async (endpoint: string, instanceId: number, setDataTable: Function, setPieChartSeries: Function, transformData: (item: any) => {
        name: string,
        waittime: number,
        key: string
    }, urlString: string, isActive: boolean = true) => {
        let data: any[] = [], pieChartSeries: PieChartSeries[] = [];

        try {
            const response = await ActivityApi.getDimension(endpoint, buildApiQuery(timeRangeContext.timeRange), instanceId, `${urlString}${filterParameters}`);

            if (!isActive) return;

            const sortedData = response.data.sort((a: any, b: any) => {
                const aWait = Number(a.waittime) || 0;
                const bWait = Number(b.waittime) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait; // Descending order for waittime
                }

                const key = transformData(a).key;
                const aVal = a[key];
                const bVal = b[key];

                // Handle sorting for numbers and strings
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return aVal - bVal; // Numeric comparison
                }

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return aVal.localeCompare(bVal); // String comparison
                }

                // If one is number and the other is string, put numbers first
                return (typeof aVal === 'number' ? -1 : 1);
            });

            sortedData.forEach((item: any, index: number) => {
                const { name, waittime, key } = transformData(item);

                data.push({
                    id: index,
                    color: CHART_COLOURS_STATEMENTS[index],
                    [key]: name,
                    waittime
                });

                if (index < Number(process.env.REACT_APP_CHART_SERIES_CEILING)) {
                    pieChartSeries.push({
                        color: CHART_COLOURS_STATEMENTS[index],
                        formatted: Helper.getTimeInEnglish(waittime),
                        name,
                        y: waittime
                    });
                }
            });

            if (isActive) {
                setDataTable(data);
                setPieChartSeries(pieChartSeries);
            }
        } catch (error) {
            if (!isActive) return;
            console.error(`Failed to retrieve ${endpoint}.`, error);
        }
    };

    const getDatabases = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        void fetchData(
            'database',
            instanceId,
            setDatabasesDataTable,
            setDatabasesPieChartSeries,
            (item: Database) => ({
                name: item.database || '-',
                waittime: item.waittime,
                key: 'database'
            }),
            urlString,
            isActive
        );
    };

    const getSessions = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        void fetchData(
            'session',
            instanceId,
            setSessionsDataTable,
            setSessionsPieChartSeries,
            (item: Session) => ({
                name: item.session || '-',
                waittime: item.waittime,
                key: 'session'
            }),
            urlString,
            isActive
        );
    };

    const getPrograms = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        void fetchData(
            'program',
            instanceId,
            setProgramsDataTable,
            setProgramsPieChartSeries,
            (item: Program) => ({
                name: item.program || '-',
                waittime: item.waittime,
                key: 'program'
            }),
            urlString,
            isActive
        );
    };

    const getUsers = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        void fetchData(
            'username',
            instanceId,
            setUsersDataTable,
            setUsersPieChartSeries,
            (item: User) => ({
                name: item.username || '-',
                waittime: item.waittime,
                key: 'username'
            }),
            urlString,
            isActive
        );
    };

    const getClients = async (instanceId: number, urlString: string, isActive: boolean = true) => {
        void fetchData(
            'client',
            instanceId,
            setClientDataTable,
            setClientsPieChartSeries,
            (item: Client) => ({
                name: item.client || '-',
                waittime: item.waittime,
                key: 'client'
            }),
            urlString,
            isActive
        );
    };

    let pieChartOptions: ChartOption[] = [];

    // if (statementType.type === 'groupedbatch') {
    //     pieChartOptions.push({
    //         optionId: 0,
    //         key: 'Grouped Statements',
    //         data: groupStatementsPieChartSeries
    //     });
    // }
    //
    //
    // if (statementType.type === 'batch' || statementType.type === 'groupedstatement') {
    //     pieChartOptions.push({
    //         optionId: 0,
    //         key: 'Statements',
    //         data: statementsPieChartSeries
    //     });
    // }


    pieChartOptions.push(
        { optionId: 0, key: 'Waits', data: waitsPieChartSeries },
        { optionId: 0, key: (instance.type === INSTANCE_TYPE_ORACLE) ? 'Schemas' : 'Databases', data: databasesPieChartSeries },
        { optionId: 0, key: 'Sessions', data: sessionsPieChartSeries },
        { optionId: 0, key: 'Clients', data: clientsPieChartSeries },
        { optionId: 0, key: 'Users', data: usersPieChartSeries },
        { optionId: 0, key: 'Programs', data: programsPieChartSeries }
    );


    const toggleHostMetricChart = () => {
        setDisplayHostMetrics(!displayHostMetrics)
    }

    const toggleTimeChartData = (timeChartDataOption: number) => {
        const selectedOption = optionsList.find(({ value }) => value === timeChartDataOption);
        const timeChartDataTitle = selectedOption?.label || "Unknown Chart";

        setTimeChartDataOption(timeChartDataOption);
        setTimeChartDataTitle(timeChartDataTitle);
    };

    const changes = statementCombinedChanges.filter(item => item.change);
    const events = statementCombinedChanges.filter(item => item.custom);
    const alerts = statementCombinedChanges.filter(item => item.alert);

    const widgetsComponent = useMemo(() => (
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 flexlist">
            <WidgetCard
                name="Total Time"
                type='time'
                iconClassName="time"
                icon="fa-clock"
                value={statementTime}
                previous={statementTimePrevious}
                cardTooltipText="The total time spent on this statement"
            />

            <WidgetCard
                name="Events"

                events={{
                    alerts: alerts.length,
                    changes: changes.length,
                    custom: events.length
                }

                }
            />
        </div>
    ), [statementTime, statementTimePrevious, alerts.length, changes.length, events.length]);


    const isDefault = (type: string) => {

        if (type === 'waits' && (statementType.type === 'statement')) {
            return true
        }
        return visibleTabs.indexOf(type) === 0
    }

    const badgeStyle = (filterType: FilterType): string => {
        // TODO Find out why the options array becomes empty...
        return filterType < currentFilterOptions.length &&
            currentFilterOptions[filterType].filters.length > 0 ?
            "badge bg-filter" : "badge bg-info";
    }

    const tableTabsComponent = useMemo(() => (
        <div className="row row-cols-1">
            {/* Tab Headings */}
            <div id={`${tabIdPrefix}menu-inner`} className="col nav nav-tabs menu-top" role="tablist">

                {/*Show grouped statements for group Batch Activity*/}
                <ConditionalRender if={visibleTabs.indexOf('groupedstatements') > -1}>
                    <button type="button" id={`${tabIdPrefix}groupedstatements-tab`} data-bs-toggle="tab"
                        className={isDefault('groupedstatements') ? 'active' : ''}
                        data-bs-target={`#${tabIdPrefix}groupedstatements`}
                        role="tab"
                        aria-controls={`${tabIdPrefix}groupedstatements`} aria-selected={isDefault('groupedstatements')}>Grouped Statements
                        <span className="badge bg-info"
                            data-tooltip-id="global-tooltip" data-tooltip-content="Total grouped statement count">{Helper.getFormattedBadgeCount(groupedStatements.length)}</span></button>
                </ConditionalRender>

                {/*Show  statements for Batch Activity and Grouped Statements */}
                <ConditionalRender if={visibleTabs.indexOf('statements') > -1}>
                    <button type="button" id={`${tabIdPrefix}statements-tab`}
                        className={isDefault('statements') ? 'active' : ''}
                        data-bs-toggle="tab"
                        data-bs-target={`#${tabIdPrefix}statements`}
                        role="tab"
                        aria-controls={`${tabIdPrefix}statements`}
                        aria-selected={isDefault('statements')}>Statements
                        <span className="badge bg-info"
                            data-tooltip-id="global-tooltip" data-tooltip-content="Total associated statement count">{Helper.getFormattedBadgeCount(statements.length)}</span></button>
                </ConditionalRender>


                <button type="button" id={`${tabIdPrefix}waits-tab`}
                    className={isDefault('waits') ? 'active' : ''}
                    data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}waits`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}waits`}
                    aria-selected={isDefault('waits')}>Waits
                    <span className={badgeStyle(FilterType.Waits)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total wait state count">{Helper.getFormattedBadgeCount(waitsDataTable.length)}</span></button>

                <button type="button" id={`${tabIdPrefix}databases-tab`} data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}databases`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}databases`}
                    aria-selected="false">{((instance.type === INSTANCE_TYPE_ORACLE) ? 'Schemas' : 'Databases')}
                    <span className={badgeStyle(FilterType.Databases)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total database count">{Helper.getFormattedBadgeCount(databasesDataTable.length)}</span></button>

                <button type="button" id={`${tabIdPrefix}sessions-tab`} data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}sessions`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}sessions`} aria-selected="false">Sessions
                    <span className={badgeStyle(FilterType.Sessions)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total session count">{Helper.getFormattedBadgeCount(sessionsDataTable.length)}</span></button>

                <button type="button" id={`${tabIdPrefix}clients-tab`} data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}clients`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}clients`}
                    aria-selected="false">Clients
                    <span className={badgeStyle(FilterType.Clients)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total client count">{Helper.getFormattedBadgeCount(clientsDataTable.length)}</span></button>

                <button type="button" id={`${tabIdPrefix}users-tab`} data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}users`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}users`}
                    aria-selected="false">Users
                    <span className={badgeStyle(FilterType.Users)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total user count">{Helper.getFormattedBadgeCount(usersDataTable.length)}</span></button>

                <button type="button" id={`${tabIdPrefix}programs-tab`} data-bs-toggle="tab"
                    data-bs-target={`#${tabIdPrefix}programs`}
                    role="tab"
                    aria-controls={`${tabIdPrefix}programs`} aria-selected="false">Programs
                    <span className={badgeStyle(FilterType.Programs)}
                        data-tooltip-id="global-tooltip" data-tooltip-content="Total program count">{Helper.getFormattedBadgeCount(programsDataTable.length)}</span></button>
            </div>

            <div className="tab-content">
                {/*Show grouped statements for group Batch Activity*/}
                <ConditionalRender if={visibleTabs.indexOf('groupedstatements') > -1}>
                    <GroupedStatementsTable filtersParameters={filterParameters}
                        instance={instance} instanceTime={statementTime} batchId={batchId}
                        showByDefault={isDefault('groupedstatements')}
                        waits={waitsDataTable}
                        statementsDataTable={groupedStatementsDataTable}
                        tabIdPrefix={tabIdPrefix}
                        onStatementClick={!tabIdPrefix ? openPanel : undefined} />
                </ConditionalRender>

                <ConditionalRender if={visibleTabs.indexOf('statements') > -1}>
                    <StatementsTable instance={instance}
                        instanceTime={statementTime}
                        batchId={batchId}
                        batchStatementId={batchStatementId}
                        showByDefault={isDefault('statements')}
                        waits={waitsDataTable}
                        statementsDataTable={statementsDataTable}
                        tabIdPrefix={tabIdPrefix}
                        onStatementClick={!tabIdPrefix ? openPanel : undefined} />
                </ConditionalRender>

                {/* show waits as default tab only if is a single statement */}
                <WaitsTable loading={loading} instance={instance} instanceTime={statementTime} waitsDataTable={waitsDataTable} setFilterOptions={setFilterOptions}
                    isDefault={!statementType.isGroup && !statementType.isBatch} tabIdPrefix={tabIdPrefix} />

                <DatabasesTable instance={instance} batchId={batchId} statementId={statementId} instanceTime={statementTime} databasesDataTable={databasesDataTable}
                    setFilterOptions={setFilterOptions} tabIdPrefix={tabIdPrefix} />

                <SessionsTable instanceId={instance.id} batchId={batchId} statementId={statementId} instanceTime={statementTime} sessionsDataTable={sessionsDataTable}
                    setFilterOptions={setFilterOptions} tabIdPrefix={tabIdPrefix} />

                <ClientsTable clientsDataTable={clientsDataTable} instanceId={instance.id} batchId={props.batchId} statementId={statementId} instanceTime={statementTime}
                    setFilterOptions={props.setFilterOptions} tabIdPrefix={tabIdPrefix} />

                <UsersTable instanceId={instance.id} batchId={batchId} statementId={statementId} instanceTime={statementTime} usersDataTable={usersDataTable}
                    setFilterOptions={setFilterOptions} tabIdPrefix={tabIdPrefix} />

                <ProgramsTable instanceId={instance.id} batchId={batchId} statementId={statementId} instanceTime={statementTime} programsDataTable={programsDataTable}
                    setFilterOptions={setFilterOptions} tabIdPrefix={tabIdPrefix} />
            </div>
        </div>
    ), [waitsDataTable, databasesDataTable, sessionsDataTable, clientsDataTable, usersDataTable, programsDataTable, statementsDataTable, groupedStatementsDataTable, tabIdPrefix, openPanel]);

    const pieChartsComponent = useMemo(() => (<div>
        {waitsPieChartSeries.length > 0 && clientsPieChartSeries.length > 0 && pieChartOptions.length && (
            <div className="row row-cols-1 row-cols-lg-2">
                <PieChart filtersOptions={currentFilterOptions} uniqueIndex={0}
                    chartOptions={pieChartOptions}
                    instanceType={instance.type}
                    loading={loading}
                    defaultChartOption={pieChartOptions.filter(option => option.key === 'Waits')[0]}
                    setFilterOptions={setFilterOptions} />
                <PieChart filtersOptions={currentFilterOptions} uniqueIndex={1}
                    chartOptions={pieChartOptions}
                    loading={loading}
                    instanceType={instance.type}
                    defaultChartOption={pieChartOptions.filter(option => option.key === 'Clients')[0]}
                    setFilterOptions={setFilterOptions} />
            </div>
        )}
    </div>
    ), [setFilterOptions, pieChartOptions]);

    // Check if we have baseline parameters from URL
    const hasBaselineParams = waitTimeParam > 0 || baselineWaitTimeParam > 0;

    // Create flat line data for baseline metrics
    const waitTimeFlatLine = useMemo(() => {
        if (waitTimeParam > 0 && activityTimeSeries.length > 0) {
            return activityTimeSeries.map(item => [item[0], waitTimeParam]);
        }
        return [];
    }, [activityTimeSeries, waitTimeParam]);

    const baselineWaitTimeFlatLine = useMemo(() => {
        if (baselineWaitTimeParam > 0 && activityTimeSeries.length > 0) {
            return activityTimeSeries.map(item => [item[0], baselineWaitTimeParam]);
        }
        return [];
    }, [activityTimeSeries, baselineWaitTimeParam]);

    // Create flat shaded areas for baseline standard deviations (ABOVE baseline)
    const baselineFlatRange1Series = useMemo(() => {
        if (baselineWaitTimeParam > 0 && baselineStdDevWaitTimeParam > 0 && activityTimeSeries.length > 0) {
            return activityTimeSeries.map(item => [
                item[0],
                baselineWaitTimeParam,
                baselineWaitTimeParam + baselineStdDevWaitTimeParam
            ]);
        }
        return [];
    }, [activityTimeSeries, baselineWaitTimeParam, baselineStdDevWaitTimeParam]);

    const baselineFlatRange2Series = useMemo(() => {
        if (baselineWaitTimeParam > 0 && baselineStdDevWaitTimeParam > 0 && activityTimeSeries.length > 0) {
            return activityTimeSeries.map(item => [
                item[0],
                baselineWaitTimeParam,
                baselineWaitTimeParam + 2 * baselineStdDevWaitTimeParam
            ]);
        }
        return [];
    }, [activityTimeSeries, baselineWaitTimeParam, baselineStdDevWaitTimeParam]);

    const baselineFlatRange3Series = useMemo(() => {
        if (baselineWaitTimeParam > 0 && baselineStdDevWaitTimeParam > 0 && activityTimeSeries.length > 0) {
            return activityTimeSeries.map(item => [
                item[0],
                baselineWaitTimeParam,
                baselineWaitTimeParam + 3 * baselineStdDevWaitTimeParam
            ]);
        }
        return [];
    }, [activityTimeSeries, baselineWaitTimeParam, baselineStdDevWaitTimeParam]);

    // Calculate peak for baseline comparison chart
    // Baseline Comparison Chart
    const baselineTooltip = useCallback((params: any[]) => {
        let data = '';

        params.forEach((point: any) => {
            if (point.data?.low !== undefined && point.data?.high !== undefined) {
                data += tooltipRow(point.color, point.seriesName,
                    `${Helper.getTimeInEnglish(point.data.low)} - ${Helper.getTimeInEnglish(point.data.high)}`);
            } else if (point.value?.[1] !== undefined) {
                data += tooltipRow(point.color, point.seriesName, Helper.getTimeInEnglish(point.value[1]));
            }
        });

        return wrapTooltip(params?.[0]?.axisValue, data);
    }, []);

    const baselineComparisonChart = useMemo(() => {
        if (!hasBaselineParams || activityTimeSeries.length === 0) {
            return null;
        }

        const series: EChartTimeSeries[] = [];

        if (baselineFlatRange3Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#fff3e0',
                data: baselineFlatRange3Series as EChartBandPoint[],
                name: 'Baseline +3σ',
                fillOpacity: 0.6,
            });
        }

        if (baselineFlatRange2Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#ffe0b2',
                data: baselineFlatRange2Series as EChartBandPoint[],
                name: 'Baseline +2σ',
                fillOpacity: 0.7,
            });
        }

        if (baselineFlatRange1Series.length > 0) {
            series.push({
                chartType: 'band',
                color: '#ffcc80',
                data: baselineFlatRange1Series as EChartBandPoint[],
                name: 'Baseline +1σ',
                fillOpacity: 0.8,
            });
        }

        if (baselineWaitTimeFlatLine.length > 0) {
            series.push({
                chartType: 'line',
                color: '#ff9800',
                data: baselineWaitTimeFlatLine as EChartPoint[],
                name: 'Baseline Wait Time',
            });
        }

        if (waitTimeFlatLine.length > 0) {
            series.push({
                chartType: 'line',
                color: '#9c27b0',
                data: waitTimeFlatLine as EChartPoint[],
                name: 'Current Wait Time',
            });
        }

        series.push({
            chartType: 'line',
            color: '#69d1bf',
            data: activityTimeSeries as EChartPoint[],
            name: 'Wait Time',
        });

        return <TimeSeriesChart series={series} tooltipType={CHART_DATA_TYPE.TIME}
                                plotLines={statementChartPlotLines} legend={true}
                                tooltipFormatter={baselineTooltip}/>;

    }, [hasBaselineParams, activityTimeSeries, waitTimeFlatLine, baselineWaitTimeFlatLine, baselineFlatRange1Series, baselineFlatRange2Series, baselineFlatRange3Series, statementChartPlotLines, baselineTooltip]);

    return (
        <div>
            {widgetsComponent}

            {/* Show filter bar when in panel mode */}
            {tabIdPrefix && (
                <div className="row mb-2">
                    <FilterBar setFilterOptions={setFilterOptions} />
                </div>
            )}

            <div className="row">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                            Time spent
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                data-bs-target="#collapseExecutions" aria-expanded="false"
                                aria-controls="collapseExecutions" />
                            <div className="btn-group float-end">
                                {availableHosts.length > 0 && (
                                    <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                                        onClick={() => toggleHostMetricChart()}>
                                        {((displayHostMetrics) ? 'Hide Hosts' : 'Show Hosts')}
                                    </button>
                                )}

                                <button id="btnChartList" type="button"
                                    className="btn btn-xsm btn-primary dropdown-toggle float-end ms-1"
                                    data-bs-toggle="dropdown" aria-expanded="false">
                                    {timeChartDataTitle}
                                </button>
                                <ul className="dropdown-menu border" aria-labelledby="btnChartList">
                                    <li>
                                        {optionsList.map(({ label, value }) => (
                                            <span
                                                key={value}
                                                className={`dropdown-item ${timeChartDataOption === value ? "active" : ""}`}
                                                role="button"
                                                onClick={() => toggleTimeChartData(value)}
                                            >
                                                {label}
                                            </span>
                                        ))}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div id="collapseExecutions" className="card-body collapse show">
                            <StatementTimeChart instance={instance}
                                batchId={batchId}
                                dataOption={timeChartDataOption}
                                waits={waitsDataTable}
                                clients={clientsDataTable}
                                programs={programsDataTable}
                                sessions={sessionsDataTable}
                                databases={databasesDataTable}
                                users={usersDataTable}
                                statementId={statementId}
                                isGroup={statementType.isGroup}
                                filtersParameters={filterParameters}
                               
                                batchStatementId={batchStatementId}
                                plotLines={statementChartPlotLines}
                                statementType={statementType}
                            />
                            {displayHostMetrics && (
                                <HostsOverTimeCharts instance={instance} hosts={hosts}
                                   
                                    plotLines={statementChartPlotLines} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

                {hasBaselineParams && (
                    <div className="row">
                        <div className="col">
                            <div className="card collapsible">
                                <div className="card-header">
                                    <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                                    Baseline Comparison
                                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                        data-bs-target="#collapse-baseline-comparison" aria-expanded="false"
                                        aria-controls="collapse-baseline-comparison" />
                                </div>

                                <div id="collapse-baseline-comparison" className="card-body collapse show">
                                    <ConditionalRender if={loading && !isAutorefresh}>
                                        <div className="w-100 text-center text-muted mt-3">
                                            <div className="loader spinner chartSpinner">
                                            </div>
                                            <p className="mt-3">
                                                Loading data...
                                            </p>
                                        </div>
                                    </ConditionalRender>
                                    <ConditionalRender if={isAutorefresh || !loading}>
                                        {activityTimeSeries.length > 0 && (
                                            <>
                                                <p className="chart-peak">
                                                    {waitTimeParam > 0 && (
                                                        <>Current Wait Time: {Helper.getTimeInEnglish(waitTimeParam)}</>
                                                    )}
                                                    {waitTimeParam > 0 && baselineWaitTimeParam > 0 && ', '}
                                                    {baselineWaitTimeParam > 0 && (
                                                        <>Baseline Wait Time: {Helper.getTimeInEnglish(baselineWaitTimeParam)}</>
                                                    )}
                                                    {baselineStdDevWaitTimeParam > 0 && (
                                                        <>, Baseline Std Dev: {Helper.getTimeInEnglish(baselineStdDevWaitTimeParam)}</>
                                                    )}
                                                </p>
                                                <br />
                                                {baselineComparisonChart}
                                            </>
                                        )}
                                        {activityTimeSeries.length === 0 && !loading && (
                                            <p className="text-muted">No wait time data available</p>
                                        )}
                                    </ConditionalRender>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            {pieChartsComponent}
            {tableTabsComponent}

            {/* Statement Details Slide-out Panel (only when not already in a panel) */}
            {!tabIdPrefix && selectedStatement && (
                <StatementDetailsPanel
                    key={selectedStatement}
                    statementId={selectedStatement}
                    open={panelOpen}
                    instance={instance}
                    onClose={closePanel}
                    statementType={selectedStatementType}
                />
            )}
        </div>
    )
}

StatementActivity.propTypes = {};

export default StatementActivity;